<template>
  <div class="signup-container">
    <div class="signup-wrapper">
      <!-- Left side - Image/Branding -->
      <div class="signup-left">
        <div class="brand-content">
          <div class="logo-section">
            <h1 class="brand-title">Gstocky</h1>
            <p class="brand-subtitle">{{ $t('manage_your_business') }}</p>
          </div>
          <div class="features-list">
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('inventory_management') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('crm_customer_relations') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('sales_invoicing') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('purchases_suppliers') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('point_of_sale') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('payment_management') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('attachments_documents') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('subscriptions_billing') }}</span>
            </div>
            <div class="feature-item">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              <span>{{ $t('and_much_more') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Signup Form -->
      <div class="signup-right">
        <div class="signup-form-container">
          <div v-if="!sent" class="form-header">
            <div class="mobile-brand">Gstocky</div>
            <p class="form-subtitle">{{ $t('create_account_start_today') }}</p>
          </div>

          <div v-if="sent" class="success-message">
            <div class="success-icon animated-icon">
              <AppIcon name="bi-envelope-check-fill" class="bi bi-envelope-check-fill" />
            </div>
            <h3>{{ $t('account_almost_ready') }}</h3>
            <p class="success-desc">{{ $t("confirmation_link_sent_to_your_Email") }}</p>
            
            <div class="spam-notice">
              <AppIcon name="bi-info-circle-fill" class="bi bi-info-circle-fill" />
              <span>{{ $t('check_spam_folder') }}</span>
            </div>
            
            <router-link to="/login" class="back-login-btn">
              {{ $t('go_to_login') }}
            </router-link>
          </div>

          <form v-else @submit.prevent="signUp" class="signup-form">
            <GoogleLoginButton
              text="signup_with"
              @success="signUpWithGoogle"
              @error="showGoogleError"
            />

            <div class="auth-divider">
              <span>{{ $t('or') }}</span>
            </div>

            <div class="form-group">
              <label for="name" class="form-label">
                {{ $t("name") }} <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <AppIcon name="bi-person" class="bi bi-person input-icon" />
                <input 
                  id="name"
                  type="text" 
                  v-model="name" 
                  :placeholder="$t('name')"
                  class="form-input"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="email" class="form-label">
                {{ $t("email") }} <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <AppIcon name="bi-envelope" class="bi bi-envelope input-icon" />
                <input 
                  id="email"
                  type="email" 
                  v-model="email" 
                  :placeholder="$t('email')"
                  class="form-input"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="phone" class="form-label">
                {{ $t("phone") }} <span class="required">*</span>
              </label>
              <div class="input-wrapper phone-input-wrapper">
                <div ref="countryPicker" class="country-code-picker">
                  <button
                    type="button"
                    class="country-code-trigger"
                    aria-haspopup="listbox"
                    :aria-expanded="countryDropdownOpen ? 'true' : 'false'"
                    @click="toggleCountryDropdown"
                  >
                    <span>{{ selectedCountry.flag }} {{ selectedCountry.dialCode }}</span>
                    <AppIcon name="bi-chevron-down" class="bi bi-chevron-down" />
                  </button>

                  <div v-if="countryDropdownOpen" class="country-code-menu">
                    <div class="country-search-wrapper">
                      <AppIcon name="bi-search" class="bi bi-search" />
                      <input
                        ref="countrySearchInput"
                        v-model="countrySearch"
                        type="search"
                        class="country-search-input"
                        :placeholder="$t('search_country')"
                        autocomplete="off"
                        @keydown.esc.stop="closeCountryDropdown"
                      />
                    </div>

                    <div class="country-options" role="listbox">
                      <button
                        v-for="country in filteredCountryCodes"
                        :key="country.iso"
                        type="button"
                        class="country-option"
                        :class="{ selected: country.iso === selectedCountryIso }"
                        role="option"
                        :aria-selected="country.iso === selectedCountryIso ? 'true' : 'false'"
                        @click="selectCountry(country.iso)"
                      >
                        <span class="country-option-name">
                          <span class="country-flag">{{ country.flag }}</span>
                          {{ country.name }}
                        </span>
                        <span class="country-option-code">{{ country.dialCode }}</span>
                      </button>

                      <div v-if="!filteredCountryCodes.length" class="country-empty">
                        {{ $t('no_countries_found') }}
                      </div>
                    </div>
                  </div>
                </div>
                <input 
                  id="phone"
                  type="tel" 
                  v-model="phone" 
                  :placeholder="$t('phone')"
                  class="form-input phone-input"
                  inputmode="tel"
                  autocomplete="tel-national"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="password" class="form-label">
                {{ $t("password") }} <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <AppIcon name="bi-lock" class="bi bi-lock input-icon" />
                <input 
                  id="password"
                  :type="showPassword ? 'text' : 'password'" 
                  v-model="password" 
                  :placeholder="$t('password')"
                  class="form-input"
                  required
                />
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                >
                  <AppIcon :name="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'" :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'" />
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              class="signup-btn" 
              :disabled="loading"
              :class="{ 'loading': loading }"
            >
              <span v-if="loading" class="loading-spinner"></span>
              <span v-else>{{ $t("signup") }}</span>
            </button>

            <p class="terms-note">
              {{ $t('accept_terms_prefix') }}
              <a href="https://gstocky.com/terms" target="_blank">
                {{ $t('accept_terms_link') }}
              </a>
              {{ $t('accept_terms_suffix') }}
            </p>
          </form>

          <div class="login-link">
            {{ $t('already_have_account') }} 
            <router-link to="/login" class="login-link-btn">{{ $t('login') }}</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from "../helper";
import GoogleLoginButton from "../components/GoogleLoginButton.vue";
import {
  clearStoredPlanIntent,
  getStoredPlanIntent,
  savePlanIntentFromQuery,
} from "@/constants/pricingPlans";
import {
  COUNTRY_CALLING_CODES,
  DEFAULT_COUNTRY_ISO,
  getBrowserCountryIso,
} from "@/constants/countryCallingCodes";

export default {
  name: "Signup",
  components: {
    GoogleLoginButton,
  },
  mounted() {
    this.capturePlanIntent();
    this.detectVisitorCountry();
    document.addEventListener("click", this.handleCountryPickerClickOutside);
    window.mixpanel.track("signup");
    document.title = "Créer un Compte | Gstocky";
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleCountryPickerClickOutside);
  },
  data() {
    return {
      email: "",
      password: "",
      name: "",
      phone: "",
      selectedCountryIso: getBrowserCountryIso(),
      countryDropdownOpen: false,
      countrySearch: "",
      countryManuallySelected: false,
      loading: false,
      sent: false,
      showPassword: false,
      resendLoading: false,
    };
  },
  computed: {
    countryCodes() {
      const browserLocale = typeof navigator !== "undefined" ? navigator.language : "en";
      const locale = this.$i18n?.locale || browserLocale || "en";
      let displayNames;

      try {
        displayNames = typeof Intl.DisplayNames === "function"
          ? new Intl.DisplayNames([locale], { type: "region" })
          : null;
      } catch (error) {
        displayNames = null;
      }

      return COUNTRY_CALLING_CODES.map((country) => ({
        ...country,
        name: displayNames?.of(country.iso) || country.iso,
      }));
    },
    selectedCountry() {
      return this.countryCodes.find((country) => country.iso === this.selectedCountryIso)
        || this.countryCodes.find((country) => country.iso === DEFAULT_COUNTRY_ISO);
    },
    filteredCountryCodes() {
      const query = this.normalizeCountrySearch(this.countrySearch);
      if (!query) return this.countryCodes;

      return this.countryCodes.filter((country) => this.normalizeCountrySearch(
        `${country.name} ${country.iso} ${country.dialCode}`
      ).includes(query));
    },
  },
  methods: {
    capturePlanIntent() {
      const intent = savePlanIntentFromQuery(this.$route.query);
      if (!intent) clearStoredPlanIntent();
    },

    async detectVisitorCountry() {
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/location/country`,
          { silent: true, timeout: 4000 }
        );
        const countryIso = response.data?.country_code?.toUpperCase();
        const isSupported = this.countryCodes.some((country) => country.iso === countryIso);

        if (!this.countryManuallySelected && isSupported) {
          this.selectedCountryIso = countryIso;
        }
      } catch (error) {
        // Browser locale detection, then Morocco, are already applied as fallbacks.
      }
    },

    normalizeCountrySearch(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase();
    },

    toggleCountryDropdown() {
      this.countryDropdownOpen = !this.countryDropdownOpen;
      if (!this.countryDropdownOpen) return;

      this.countrySearch = "";
      this.$nextTick(() => this.$refs.countrySearchInput?.focus());
    },

    closeCountryDropdown() {
      this.countryDropdownOpen = false;
      this.countrySearch = "";
    },

    selectCountry(countryIso) {
      this.selectedCountryIso = countryIso;
      this.countryManuallySelected = true;
      this.closeCountryDropdown();
    },

    handleCountryPickerClickOutside(event) {
      if (this.$refs.countryPicker && !this.$refs.countryPicker.contains(event.target)) {
        this.closeCountryDropdown();
      }
    },

    async signUp() {
      if (!this.validateForm()) {
        return;
      }

      /* window.grecaptcha.ready(() => {
        this.loading = true;
        window.grecaptcha
          .execute("6LetDZEmAAAAAEr9EdTVoDeg6-saEQYhXVSlrQhO", {
            action: "submit",
          })
          .then(async (token) => {
            if (token) { */
              try {
                this.sent = false;
                this.loading = true;
                await helper.customPost(
                  `${process.env.VUE_APP_API}/register`,
                  {
                    email: this.email,
                    password: this.password,
                    name: this.name,
                    phone: this.formatPhoneNumber(),
                    plan_intent: getStoredPlanIntent(),
                    // token: token,
                  }
                );
                
                if (window.fbq) {
                  window.fbq('track', 'CompleteRegistration');
                }

                this.loading = false;
                this.sent = true;
              } catch (error) {
                this.loading = false;
                this.showError(error.response?.data?.message || "Une erreur s'est produite");
              }
            /* }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
            this.showError("Erreur de vérification reCAPTCHA");
          });
      }); */
    },

    async signUpWithGoogle(credential) {
      try {
        this.loading = true;
        const response = await helper.customPost(`${process.env.VUE_APP_API}/auth/google`, {
          credential,
          plan_intent: getStoredPlanIntent()
        });

        if (response.data.is_new_user && window.fbq) {
          window.fbq('track', 'CompleteRegistration');
        }

        if (response.data.two_factor_required) {
          this.loading = false;
          this.$router.push({ path: '/2fa', query: { token: response.data.temp_token } });
          return;
        }

        this.loading = false;
        this.$store.commit('SET_TOKEN', response.data.access_token);
        this.$store.commit('SET_REFRESH_TOKEN', response.data.refresh_token);
        helper.setUserConnectedCookie();
        this.showSuccess(this.$t('logged_in'));
        window.location.href = "/";
      } catch (error) {
        this.loading = false;
        this.showGoogleError(error);
      }
    },

    showGoogleError(error) {
      this.showError(error.response?.data?.message || this.$t('google_signin_failed'));
    },

    validateForm() {
      if (!this.name.trim() || !this.email.trim() || !this.password.trim() || !this.phone.trim()) {
        this.showError(this.$t("please_complete_all_required_fields"));
        return false;
      }

      if (!this.isValidEmail(this.email)) {
        this.showError("Adresse email invalide");
        return false;
      }

      if (this.password.length < 6) {
        this.showError("Le mot de passe doit contenir au moins 6 caractères");
        return false;
      }

      return true;
    },

    async resendEmail() {
      this.resendLoading = true;
      try {
        await helper.customPost(`${process.env.VUE_APP_API}/resend-verification`, {
          email: this.email
        });
        this.showSuccess("Email de vérification renvoyé");
      } catch (error) {
        this.showError("Erreur lors de l'envoi de l'email");
      }
      this.resendLoading = false;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    formatPhoneNumber() {
      const phone = this.phone.trim();

      if (phone.startsWith("+")) {
        return `+${phone.slice(1).replace(/\D/g, "")}`;
      }

      const nationalNumber = phone.replace(/\D/g, "").replace(/^0+/, "");
      return `${this.selectedCountry.dialCode}${nationalNumber}`;
    },

    showError(message) {
      window.vueOpener.open({
        message: message,
        type: "error",
        position: "bottom",
      });
    },

    showSuccess(message) {
      window.vueOpener.open({
        message: message,
        type: "success",
        position: "bottom",
      });
    },
  },
};
</script>

<style scoped>
.signup-container {
  min-height: 100vh;
  /* background: #004aad; */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.signup-wrapper {
  display: flex;
  max-width: 1200px;
  width: 100%;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 14, 31, 0.15);
}

.signup-left {
  flex: 1;
  background: #004aad;
  color: white;
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-content {
  text-align: center;
}

.brand-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 10px;
  background: linear-gradient(45deg, #fff, #e6f0ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-subtitle {
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.features-list {
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  font-size: 1.1rem;
}

.feature-item .app-lucide-icon {
  color: #00d4aa;
  flex: 0 0 auto;
  font-size: 1.3rem;
}

.signup-right {
  flex: 1;
  padding: 60px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signup-form-container {
  width: 100%;
  max-width: 400px;
}

.form-header {
  text-align: center;
  margin-bottom: 18px;
}

.mobile-brand {
  display: none;
  color: #004aad;
  font-size: 1.4rem;
  font-weight: 800;
  margin-bottom: 12px;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #004aad;
  margin-bottom: 10px;
}

.form-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.success-message {
  text-align: center;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.success-icon {
  font-size: 4rem;
  color: #00d4aa;
  margin-bottom: 20px;
}

.animated-icon {
  animation: bounceRotate 2s infinite ease-in-out;
}

@keyframes bounceRotate {
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-10px) rotate(5deg); }
}

.success-message h3 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #004aad;
  margin-bottom: 15px;
}

.success-desc {
  color: #6b7280;
  margin-bottom: 25px;
  font-size: 1.05rem;
  line-height: 1.5;
}

.spam-notice {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  text-align: left;
  gap: 12px;
  margin-bottom: 30px;
  color: #4b5563;
  font-size: 0.9rem;
  border-left: 4px solid #3b82f6;
  width: 100%;
}

.spam-notice i {
  color: #3b82f6;
  font-size: 1.2rem;
}

.back-login-btn {
  display: inline-block;
  background: #004aad;
  color: white;
  text-decoration: none;
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.3s;
  width: 100%;
}

.back-login-btn:hover {
  background: #0056d6;
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 86, 214, 0.2);
}

.resend-btn {
  background: #004aad;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s;
}

.resend-btn:hover:not(:disabled) {
  background: #0056d6;
}

.resend-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.required {
  color: #ef4444;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #004aad;
  font-size: 1.1rem;
}

.form-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  background: #f9fafb;
}

.form-input:focus {
  outline: none;
  border-color: #004aad;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 18, 40, 0.1);
}

.phone-input-wrapper {
  gap: 8px;
  align-items: stretch;
}

.country-code-picker {
  flex: 0 0 112px;
  width: 112px;
  position: relative;
}

.country-code-trigger {
  width: 100%;
  min-height: 50px;
  padding: 12px 8px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  color: #374151;
  font-size: 0.95rem;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.country-code-trigger:focus,
.country-code-trigger[aria-expanded="true"] {
  outline: none;
  border-color: #004aad;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 18, 40, 0.1);
}

.country-code-trigger .bi-chevron-down {
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.country-code-trigger[aria-expanded="true"] .bi-chevron-down {
  transform: rotate(180deg);
}

.country-code-menu {
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  width: 310px;
  max-width: calc(100vw - 40px);
  padding: 8px;
  border: 1px solid #dbe3ed;
  border-radius: 10px;
  background: white;
  box-shadow: 0 14px 35px rgba(15, 23, 42, 0.18);
}

.country-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.country-search-wrapper .bi-search {
  position: absolute;
  left: 11px;
  color: #64748b;
}

.country-search-input {
  width: 100%;
  padding: 9px 10px 9px 34px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #1f2937;
  font-size: 0.9rem;
}

.country-search-input:focus {
  outline: none;
  border-color: #004aad;
  box-shadow: 0 0 0 2px rgba(0, 74, 173, 0.12);
}

.country-options {
  max-height: 240px;
  overflow-y: auto;
}

.country-option {
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
}

.country-option:hover,
.country-option.selected {
  background: #eef5ff;
  color: #004aad;
}

.country-option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.country-flag {
  display: inline-block;
  width: 24px;
}

.country-option-code {
  flex-shrink: 0;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.country-empty {
  padding: 16px 10px;
  color: #64748b;
  font-size: 0.88rem;
  text-align: center;
}

.phone-input {
  min-width: 0;
  padding-left: 12px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #334155;
  cursor: pointer;
  font-size: 1.1rem;
}

.password-toggle:hover {
  color: #0056d6;
}

.signup-btn {
  background: #004aad;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signup-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 12, 27, 0.3);
}

.signup-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6b7280;
  font-size: 0.85rem;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.login-link {
  text-align: center;
  margin-top: 30px;
  color: #6b7280;
  font-size: 0.9rem;
}

.login-link-btn {
  color: #004aad;
  text-decoration: none;
  font-weight: 600;
}

.login-link-btn:hover {
  text-decoration: underline;
}

.terms-note {
  color: #6b7280;
  font-size: 0.82rem;
  line-height: 1.4;
  margin: 0;
  text-align: center;
}

.terms-note a {
  color: #004aad;
  font-weight: 600;
  text-decoration: none;
}

.terms-note a:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .signup-container {
    min-height: 100svh;
    padding: 0;
    align-items: stretch;
    background: white;
  }

  .signup-wrapper {
    display: block;
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  .signup-left {
    display: none;
  }

  .signup-right {
    min-height: 100svh;
    padding: 28px 20px 14px;
    align-items: flex-start;
  }

  .signup-form-container {
    max-width: 460px;
    margin: 0 auto;
  }

  .mobile-brand {
    display: block;
  }

  .form-header {
    margin-bottom: 12px;
  }

  .form-title {
    font-size: 1.55rem;
    margin-bottom: 4px;
  }

  .form-subtitle {
    font-size: 0.9rem;
  }

  .signup-form {
    gap: 10px;
  }

  .form-label {
    margin-bottom: 4px;
    font-size: 0.8rem;
  }

  .form-input {
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 8px;
  }

  .country-code-picker {
    flex-basis: 104px;
    width: 104px;
  }

  .country-code-trigger {
    min-height: 46px;
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 8px;
  }

  .input-icon {
    font-size: 1rem;
  }

  .signup-btn {
    min-height: 44px;
    padding: 11px;
    border-radius: 8px;
  }

  .terms-note {
    font-size: 0.76rem;
    line-height: 1.3;
  }

  .login-link {
    margin-top: 12px;
  }

  .features-list {
    display: none;
  }
}

@media (max-width: 480px) {
  .signup-container {
    padding: 0;
  }

  .signup-right {
    padding: 24px 16px 12px;
  }

  .form-title {
    font-size: 1.35rem;
  }

  .form-subtitle {
    font-size: 0.84rem;
  }

  .signup-form {
    gap: 8px;
  }

  .form-label {
    display: none;
  }
}
</style>
